import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { CategoryNode } from '@biyolink/shared';
import { Skeleton } from '@/components/ui';
import { formatNumber } from '@/lib/format';
import { t } from '@/i18n';

/**
 * Ürün türleri şeridi — tek satır, yatay kayan (müşteri notu: biyolink.com'daki gibi).
 *
 * Önceden kategoriler ana sayfada büyük bir ızgaraydı ve 26 ürün ekranın yarısını
 * kaplayıp alt bilgideki iletişim bilgilerini çok aşağıda bırakıyordu. Tek satır
 * şerit hem yeri az kaplıyor hem de aynı ürünlerin tamamına erişim veriyor.
 */
export function CategoryStrip({
  categories,
  isLoading,
}: {
  categories: CategoryNode[] | undefined;
  isLoading: boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);

  const products = (categories ?? []).flatMap((type) => type.children);

  function scrollBy(direction: -1 | 1) {
    const node = scroller.current;
    if (!node) return;
    // Görünen genişliğin %80'i: bir sonraki grup gelirken bir kart bağlam olarak kalıyor.
    node.scrollBy({ left: direction * node.clientWidth * 0.8, behavior: 'smooth' });
  }

  return (
    <section className="container-page py-10">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{t('home.stripTitle')}</h2>
          <p className="mt-1 text-sm text-slate-600">{t('home.stripSubtitle')}</p>
        </div>

        <div className="hidden gap-2 sm:flex">
          {([-1, 1] as const).map((direction) => (
            <button
              key={direction}
              type="button"
              onClick={() => scrollBy(direction)}
              aria-label={direction === -1 ? t('home.stripPrev') : t('home.stripNext')}
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 text-slate-600 transition hover:border-primary-600 hover:text-primary-900"
            >
              <span aria-hidden="true">{direction === -1 ? '‹' : '›'}</span>
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-32 w-28 shrink-0 rounded-card" />
          ))}
        </div>
      ) : (
        <div
          ref={scroller}
          // `snap` ile kaydırma kart kenarında duruyor; yarım kart görünmüyor.
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:thin]"
        >
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/kategori/${product.slug}`}
              className="group w-28 shrink-0 snap-start overflow-hidden rounded-card border border-slate-200 bg-surface transition hover:border-primary-300 hover:shadow-md"
            >
              {/*
                KARE çerçeve (müşteri notu). Kaynak fotoğraflar dairesel kesim olduğu
                için `object-contain` kullanılıyor: `cover` köşeleri zeminle doldurup
                görseli yuvarlak gösteriyordu.
              */}
              <div className="aspect-square overflow-hidden bg-slate-50">
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    loading="lazy"
                    className="h-full w-full object-contain transition duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="grid h-full place-items-center text-2xl" aria-hidden="true">
                    ♻️
                  </div>
                )}
              </div>
              <div className="px-2 py-2 text-center">
                <p className="truncate text-xs font-medium text-slate-800 group-hover:text-primary-900">
                  {product.name}
                </p>
                <p className="text-[11px] tabular-nums text-slate-500">
                  {t('home.categoryListingCount', { count: formatNumber(product.listingCount) })}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
